import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import {
  AtSign,
  CircleAlert,
  CircleCheckBig,
  Eye,
  EyeOff,
  Folder,
  LucideIcon,
  MessageSquareMore,
  Minus,
  Plus,
  RefreshCcw,
  Trash2,
} from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { getUserInitials } from '@/components/UserProfileCard/utils'
import { Notification, NotificationType } from '@/schema'
import { cn, formatTimeAgo } from '@/utils'

import { useDocumentStore } from '../../../document'
import {
  useMarkAsRead,
  useMarkAsUnread,
  useNotificationDrawerActions,
} from '../../hooks/hooks'
import { createNotificationNavigationHandler } from '../../utils/navigationHandler'

interface NotificationItemProps {
  notification: Notification
  isPinned?: boolean
  onMarkAsRead?: (id: string) => void
  onMarkAsUnread?: (id: string) => void
}

interface IconConfig {
  mainIcon: LucideIcon
  mainColor: string
  bgColor: string
  badgeIcon?: LucideIcon
}

const getNotificationIcon = (type: NotificationType): IconConfig => {
  switch (type) {
    case NotificationType.NewComment:
      return {
        mainIcon: MessageSquareMore,
        mainColor: 'var(--Colors-Foreground-fg-brand-primary_alt)',
        bgColor: 'bg-brand-secondary',
        badgeIcon: Plus,
      }
    case NotificationType.CommentResolved:
    case NotificationType.SubtaskCompleted:
      return {
        mainIcon: MessageSquareMore,
        mainColor: 'var(--Colors-Foreground-fg-brand-primary_alt)',
        bgColor: 'bg-brand-secondary',
        badgeIcon: CircleCheckBig,
      }
    case NotificationType.UserMentioned:
      return {
        mainIcon: MessageSquareMore,
        mainColor: 'var(--Colors-Foreground-fg-brand-primary_alt)',
        bgColor: 'bg-brand-secondary',
        badgeIcon: AtSign,
      }
    case NotificationType.Deadline:
    case NotificationType.DeadlinePassed:
    case NotificationType.DeadlineApproaching:
      return {
        mainIcon: CircleAlert,
        mainColor: 'var(--Colors-Foreground-fg-error-secondary)',
        bgColor: 'bg-error-secondary',
      }
    case NotificationType.AssignedDeliverable:
      return {
        mainIcon: Folder,
        mainColor: 'var(--Colors-Foreground-fg-warning-primary)',
        bgColor: 'bg-warning-secondary',
        badgeIcon: Plus,
      }
    case NotificationType.ReassignedDeliverable:
      return {
        mainIcon: Folder,
        mainColor: 'var(--Colors-Foreground-fg-warning-primary)',
        bgColor: 'bg-warning-secondary',
        badgeIcon: Minus,
      }
    case NotificationType.SubtaskReopen:
      return {
        mainIcon: MessageSquareMore,
        mainColor: 'var(--Colors-Foreground-fg-brand-primary_alt)',
        bgColor: 'bg-brand-secondary',
        badgeIcon: RefreshCcw,
      }
    case NotificationType.SubtaskDeleted:
      return {
        mainIcon: MessageSquareMore,
        mainColor: 'var(--Colors-Foreground-fg-brand-primary_alt)',
        bgColor: 'bg-brand-secondary',
        badgeIcon: Trash2,
      }
    default:
      return {
        mainIcon: MessageSquareMore,
        mainColor: 'var(--Colors-Foreground-fg-brand-primary_alt)',
        bgColor: 'bg-brand-secondary',
      }
  }
}

export const NotificationItem = ({
  notification,
  isPinned = false,
  onMarkAsRead,
  onMarkAsUnread,
}: NotificationItemProps) => {
  const iconConfig = getNotificationIcon(notification.notification_type)
  const MainIcon = iconConfig.mainIcon
  const BadgeIcon = iconConfig.badgeIcon
  const mentionName = notification.metadata?.mentioner_name
  const hasMentioner = Boolean(mentionName)
  const { metadata } = notification

  const navigate = useNavigate()
  const timeAgo = formatTimeAgo(notification.created_at)
  const { closeDrawer } = useNotificationDrawerActions()
  const { setOpenDocument, setActiveDocumentId } = useDocumentStore()

  const isRead = notification.read_at !== null

  const { mutate: markAsRead } = useMarkAsRead()
  const { mutate: markAsUnread } = useMarkAsUnread()

  const handleToggleRead = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isRead) {
      markAsUnread(notification.id)
      onMarkAsUnread?.(notification.id)
    } else {
      markAsRead(notification.id)
      onMarkAsRead?.(notification.id)
    }
  }

  const handleNotificationClick = useCallback(() => {
    // Safety check for metadata
    if (!metadata) {
      return
    }

    const navigationHandler = createNotificationNavigationHandler(
      metadata,
      notification.notification_type,
      {
        navigate,
        setOpenDocument,
        setActiveDocumentId,
        closeDrawer: isPinned ? () => {} : closeDrawer,
        markAsRead: () => {
          if (!isRead) {
            markAsRead(notification.id)
          }
        },
      }
    )
    navigationHandler()
  }, [
    notification.id,
    notification.notification_type,
    metadata,
    navigate,
    setOpenDocument,
    setActiveDocumentId,
    closeDrawer,
    markAsRead,
    isRead,
  ])
  return (
    <button
      type="button"
      className={cn(
        'flex gap-3 p-3 border-none',
        isRead ? 'bg-transparent' : 'bg-blue-50',
        'w-full text-left rounded-md cursor-pointer'
      )}
      onClick={handleNotificationClick}
    >
      <div
        className={cn(
          'w-10 h-10 p-2.5 rounded-full relative',
          iconConfig.bgColor
        )}
      >
        {hasMentioner ? (
          <Avatar className="w-10 h-10 -mt-2.5 -ml-2.5">
            <AvatarFallback>{getUserInitials(mentionName)}</AvatarFallback>
          </Avatar>
        ) : (
          <MainIcon className="w-5 h-5" color={iconConfig.mainColor} />
        )}
        {BadgeIcon && (
          <div className="bg-white border border-gray-700 w-4 h-4 flex justify-center items-center rounded-full absolute -bottom-1 -right-0">
            <BadgeIcon className="w-3 h-3" />
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1">
        <div className="flex gap-4 items-start justify-between">
          <div className="text-primary-900 font-semibold text-sm line-clamp-2">
            {notification.title}
          </div>
          <div className="flex gap-3 items-center whitespace-nowrap">
            <div className="text-tertiary-600 font-normal text-xs">
              {timeAgo}
            </div>
            {isRead ? (
              <Eye
                className="w-5 h-5 cursor-pointer hover:opacity-70"
                color="var(--Colors-Foreground-fg-quaternary-400)"
                onClick={handleToggleRead}
              />
            ) : (
              <EyeOff
                className="w-5 h-5 cursor-pointer hover:opacity-70"
                color="var(--Colors-Foreground-fg-brand-primary_alt)"
                onClick={handleToggleRead}
              />
            )}
          </div>
        </div>

        <div className="text-tertiary-600 text-sm font-normal line-clamp-3">
          {notification.message}
        </div>
      </div>
    </button>
  )
}
